import { inject, Injectable, NgZone } from '@angular/core';
import { App } from '@capacitor/app';
import { Browser } from '@capacitor/browser';
import { Capacitor } from '@capacitor/core';
import { ToastrService } from 'ngx-toastr';
import {
  buildIdDigitalBrokerUrl,
  isIdDigitalCallbackUrl,
  isIdDigitalErrorUrl,
  parseIdDigitalCallback,
} from './id-digital-auth.util';
import { isDepositBrowserCancelUrl } from './deposit-browser-close.util';
import { resumeNativeAuthFromDeepLink } from './native-auth.util';

const ANDROID_BROWSER_CLOSE_DELAY_MS = 400;

@Injectable({
  providedIn: 'root',
})
export class IdDigitalDeepLinkService {
  private readonly ngZone = inject(NgZone);
  private readonly toast = inject(ToastrService);
  private initialized = false;
  private lastHandledUrl: string | null = null;

  async init(): Promise<void> {
    if (this.initialized) {
      return;
    }

    await this.listenAppUrlOpen();
    this.initialized = true;
    await this.consumeLaunchUrl();
  }

  async handleOpenUrl(url: string, options?: { skipNativeAuth?: boolean }): Promise<boolean> {
    if (!url || url === this.lastHandledUrl) {
      return false;
    }

    if (isDepositBrowserCancelUrl(url)) {
      this.lastHandledUrl = url;
      await this.closeBrowserSafely();
      this.ngZone.run(() => {
        this.toast.error('No se pudo iniciar el depósito');
      });
      return true;
    }

    if (isIdDigitalCallbackUrl(url)) {
      try {
        await this.handleIdDigitalCallback(url);
        this.lastHandledUrl = url;
      } catch {
        this.ngZone.run(() => {
          this.toast.error('No se pudo completar el ingreso con ID Digital');
        });
      }
      return true;
    }

    if (options?.skipNativeAuth) {
      return false;
    }

    const translatedAuthCallback = await this.resumeNativeAuth(url);
    if (!translatedAuthCallback) {
      return false;
    }

    this.lastHandledUrl = url;
    this.ngZone.run(() => {
      this.navigateToAuthCallback(translatedAuthCallback);
    });
    return true;
  }

  private async handleIdDigitalCallback(url: string): Promise<void> {
    const callback = parseIdDigitalCallback(url);
    if (isIdDigitalErrorUrl(url) || !callback) {
      await this.closeBrowserSafely();
      this.ngZone.run(() => {
        this.toast.error('No se pudo completar el ingreso con ID Digital');
      });
      return;
    }

    await this.closeBrowserSafely();
    if (Capacitor.getPlatform() === 'android') {
      // Con el Custom Tab del login aún abierto, Browser.open() se descarta en silencio
      // (BrowserControllerActivity reutiliza la instancia). Hay que dejar que termine.
      await new Promise((resolve) => setTimeout(resolve, ANDROID_BROWSER_CLOSE_DELAY_MS));
    }

    await this.openBrokerBrowser(buildIdDigitalBrokerUrl(callback.code, callback.state));
  }

  private async consumeLaunchUrl(): Promise<void> {
    if (!Capacitor.isNativePlatform()) {
      return;
    }

    const launchUrl = await this.getNativeLaunchUrl();
    if (launchUrl?.url) {
      await this.handleOpenUrl(launchUrl.url, { skipNativeAuth: true });
    }
  }

  private getNativeLaunchUrl(): Promise<{ url?: string } | undefined> {
    return App.getLaunchUrl();
  }

  private listenAppUrlOpen(): Promise<void> {
    return App.addListener('appUrlOpen', (data) => {
      void this.handleOpenUrl(data.url);
    }).then(() => undefined);
  }

  private resumeNativeAuth(url: string): Promise<string | null> {
    return resumeNativeAuthFromDeepLink(url);
  }

  private navigateToAuthCallback(url: string): void {
    if (!url) {
      return;
    }
    globalThis.location.href = url;
  }

  private openBrokerBrowser(url: string): Promise<void> {
    return Browser.open({ url });
  }

  private async closeBrowserSafely(): Promise<void> {
    try {
      await Browser.close();
    } catch {
      // Puede no haber browser activo (arranque en frío).
    }
  }
}
